/**
 * ForgotPassword Component
 * Request password reset email
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { userManagementAPI } from '@/services/api/userManagement';
import { normalizeEmail } from '@/utils/emailValidator';
import { Button } from '../ui/button';

import { useAppTranslation } from '@/contexts/TranslationContext';
export const ForgotPassword: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      await userManagementAPI.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset request failed:', err);
      // Don't show specific errors for security
      setSuccess(true); // Always show success to prevent email enumeration
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-8 md:p-10 w-full max-w-md shadow-2xl text-center">
          <div className="w-16 h-16 mx-auto mb-5 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('auth.check_your_email', 'Check Your Email')}</h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
            {t('auth.if_an_account_exists_with', 'If an account exists with')} <span className="font-medium text-gray-900 dark:text-white">{email}</span>, you will receive a password reset link shortly.
          </p>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('auth.the_link_will_expire_in', 'The link will expire in 1 hour for security reasons.')}
          </p>
          <Button
            onClick={() => navigate('/login')}
            variant="primary"
            fullWidth
          >
            {t('auth.back_to_login', 'Back to Login')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700 p-4 md:p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-6 transition-colors text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('auth.back_to_login_1', 'Back to Login')}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <Mail className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.reset_your_password', 'Reset Your Password')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('auth.enter_your_email_address_and', 'Enter your email address and we\'ll send you a link to reset your password.')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Mail className="w-4 h-4" />
              <span>{t('auth.email_address', 'Email Address')}</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(normalizeEmail(e.target.value))}
              placeholder={t('auth.enter_your_email_address', 'Enter your email address')}
              // A mis-cased address here is the worst case: this endpoint stays
              // silent by design, so the user is told to check their inbox and
              // nothing ever arrives (TRUS-386).
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              disabled={submitting}
              autoFocus
              className="w-full px-3 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={submitting || !email}
            loading={submitting}
          >
            {submitting ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        {/* Info Box */}
        <div className="flex gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mt-6">
          <AlertCircle className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            For security reasons, you will receive a confirmation message
            regardless of whether an account exists with this email.
          </p>
        </div>
      </div>
    </div>
  );
};
